/**
 * Admin Review Component - Interface for reviewing pending templates
 * 
 * Can render as a modal dialog or inline content based on asDialog prop
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Card, Flex, Text, Button, Badge, Separator, Dialog } from '@radix-ui/themes';
import { CheckIcon, Cross2Icon, Pencil1Icon } from '@radix-ui/react-icons';
import { DeviceTemplate } from '../../types';
import { adminAPI } from '../../services/api';
import { DEVICE_TYPE_LABELS } from '../../config/constants';
import DeviceIcon from '../DeviceIcon';
import TemplateFormFields from '../TemplateFormFields';

interface AdminReviewProps {
  adminKey: string;
  onClose?: () => void;
  onUpdate?: () => void;
  asDialog?: boolean;
}

const AdminReview: React.FC<AdminReviewProps> = ({ adminKey, onClose, onUpdate, asDialog = false }) => {
  const [pendingTemplates, setPendingTemplates] = useState<DeviceTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<DeviceTemplate | null>(null);

  const loadPendingTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const templates = await adminAPI.getPendingTemplates(adminKey);
      setPendingTemplates(templates);
    } catch (err: any) {
      setError(err.message || 'Failed to load pending templates');
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    loadPendingTemplates();
  }, [loadPendingTemplates]);

  const handleApprove = async (templateId: string) => {
    try {
      await adminAPI.approveTemplate(templateId, adminKey);
      setPendingTemplates(prev => prev.filter(t => t.id !== templateId));
      onUpdate?.();
      alert('✅ Template approved! It will now appear in the device library for all users.');
    } catch (err: any) {
      alert(`❌ Failed to approve: ${err.message}`);
    }
  };

  const handleReject = async (templateId: string) => {
    try {
      await adminAPI.rejectTemplate(templateId, adminKey);
      setPendingTemplates(prev => prev.filter(t => t.id !== templateId));
      alert('🗑️ Template rejected and removed from pending list.');
    } catch (err: any) {
      alert(`❌ Failed to reject: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <Box p="4">
        <Text>Loading pending templates...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p="4">
        <Text color="red">❌ {error}</Text>
        {asDialog && onClose && <Button mt="2" onClick={onClose}>Close</Button>}
      </Box>
    );
  }

  // Main content that will be wrapped in Dialog or rendered directly
  const mainContent = (
    <Flex direction="column" gap="3">
      {pendingTemplates.length === 0 ? (
        <Box p="6" style={{ textAlign: 'center', backgroundColor: 'var(--gray-2)', borderRadius: 'var(--radius-3)' }}>
          <Text size="4">🎉 No pending templates</Text>
          <Text size="2" color="gray" mt="2">All submissions have been reviewed!</Text>
        </Box>
      ) : (
        <>
          <Text size="2" color="gray">
            {pendingTemplates.length} pending template{pendingTemplates.length !== 1 ? 's' : ''} awaiting review
          </Text>

          {pendingTemplates.map(template => (
            <Card key={template.id}>
              <Flex justify="between" align="start">
                <Flex direction="column" gap="1" style={{ flex: 1 }}>
                  <Flex align="center" gap="2">
                    <DeviceIcon type={template.type} size={16} />
                    <Text weight="bold">
                      {template.brand ? `${template.brand} ${template.name}` : template.name}
                    </Text>
                    <Badge color="orange" size="1">Pending</Badge>
                  </Flex>
                  <Text size="1" color="gray">{DEVICE_TYPE_LABELS[template.type]}</Text>
                  {template.description && (
                    <Text size="1" color="gray" style={{ fontStyle: 'italic' }}>{template.description}</Text>
                  )}
                  <Flex gap="3" mt="1">
                    {template.powerInput && (
                      <Text size="1">⚡ {template.powerInput.voltage}V @ {template.powerInput.maxCurrent}A</Text>
                    )}
                    {template.powerOutputs && template.powerOutputs.length > 0 && (
                      <Text size="1">🔌 Output: {template.powerOutputs[0].voltage}V @ {template.powerOutputs[0].maxCurrent}A</Text>
                    )}
                    {template.usbOutputs && template.usbOutputs.length > 0 && (
                      <Text size="1">🔗 {template.usbOutputs.reduce((sum, u) => sum + u.count, 0)} USB ports</Text>
                    )}
                  </Flex>
                  <Text size="1" color="gray" mt="1">
                    Submitted: {new Date(template.createdAt).toLocaleString()} by {template.submittedBy || 'anonymous'}
                  </Text>
                </Flex>

                <Flex gap="2">
                  <Button
                    size="1"
                    variant="soft"
                    onClick={() => setEditingTemplate(template)}
                  >
                    <Pencil1Icon /> Edit
                  </Button>
                  <Button
                    size="1"
                    color="green"
                    onClick={() => {
                      if (window.confirm(`Approve "${template.name}" as a template?`)) {
                        handleApprove(template.id);
                      }
                    }}
                  >
                    <CheckIcon /> Approve
                  </Button>
                  <Button
                    size="1"
                    color="red"
                    variant="soft"
                    onClick={() => {
                      if (window.confirm(`Reject "${template.name}"? This cannot be undone.`)) {
                        handleReject(template.id);
                      }
                    }}
                  >
                    <Cross2Icon /> Reject
                  </Button>
                </Flex>
              </Flex>
            </Card>
          ))}
        </>
      )}

      {/* Edit Template Modal */}
      {editingTemplate && (
        <Dialog.Root open={true} onOpenChange={(open) => !open && setEditingTemplate(null)}>
          <Dialog.Content style={{ maxWidth: 700, maxHeight: '90vh', overflow: 'auto' }}>
            <Dialog.Title>Edit Template: {editingTemplate.name}</Dialog.Title>
            <Dialog.Description size="2" mb="4">
              Review and edit template details before approval
            </Dialog.Description>

            <TemplateFormFields
              template={editingTemplate}
              onChange={(updated) => setEditingTemplate({ ...editingTemplate, ...updated })}
              showTypeSelector={true}
            />

            <Separator my="4" />

            <Flex gap="2" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button
                variant="soft"
                onClick={async () => {
                  try {
                    await adminAPI.updateTemplate(editingTemplate.id, editingTemplate, adminKey);
                    alert('✅ Changes saved successfully!');
                    await loadPendingTemplates();
                  } catch (error: any) {
                    console.error('Failed to save:', error);
                    alert(`❌ Failed to save changes: ${error.message || 'Unknown error'}`);
                  }
                }}
              >
                Save Only
              </Button>
              <Button
                color="green"
                onClick={async () => {
                  if (window.confirm(`Save changes and approve "${editingTemplate.name}"?`)) {
                    try {
                      await adminAPI.updateTemplate(editingTemplate.id, editingTemplate, adminKey);
                      await handleApprove(editingTemplate.id);
                      setEditingTemplate(null);
                      await loadPendingTemplates();
                    } catch (error: any) {
                      console.error('Failed to save and approve:', error);
                      alert(`❌ Failed: ${error.message || 'Unknown error'}`);
                    }
                  }
                }}
              >
                Save & Approve
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      )}
    </Flex>
  );

  // Render as dialog or inline based on asDialog prop
  if (asDialog) {
    return (
      <Dialog.Root open={true} onOpenChange={(open) => !open && onClose?.()}>
        <Dialog.Content style={{ maxWidth: 900, maxHeight: '90vh', overflow: 'auto' }}>
          <Dialog.Title>🔐 Admin: Pending Template Review</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Review user-submitted manual device entries. Approve to add to library or reject to discard.
          </Dialog.Description>

          {mainContent}

          <Separator size="4" my="4" />

          <Flex justify="between" align="center">
            <Text size="1" color="gray">
              🔒 Admin-only view • Key: {adminKey.substring(0, 8)}...
            </Text>
            <Dialog.Close>
              <Button variant="soft" color="gray">Close</Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  // Inline rendering for tab views
  return mainContent;
};

export default AdminReview;
